import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImageFavoriteInfo } from '@models/ImageFavorite/imageFavoriteInfo.model';
import { ImageFavoriteImage } from '@models/ImageFavorite/imageFavoriteImage.model';
import { ImageLabelInfo } from '@models/ImageFavorite/imageLabelInfo.model';
import { ImageLabelProperties } from '@models/ImageFavorite/imageLabelProperties.model';
import { ImageLabelValue } from '@models/ImageFavorite/imageLabelValue.model';
import { ImageFavoriteSize, ImageLabelType, ImageSourceType } from '@models/ImageFavorite/imageFavorite.enums';
import { ImageFavoriteService } from '@services/imageFavorite.service';
import { arrowColor, formatLabelValue, LABEL_BASE_WIDTH_PX, labelBoxStyle, labelFontScale, labelTypeHasArrow, labelValueStyle, labelValueTooltip, newLabelProps, parseLabelProps } from 'app/shared/classes/labelStyling';
import moment from 'moment';
import { ImageLabelEditorModalComponent } from './image-label-editor-modal/image-label-editor-modal.component';

interface LabelDrag {
  label: ImageLabelInfo;
  mode: 'box' | 'target' | 'resize';
  startX: number;
  startY: number;
  origX: number;
  origY: number;
  stageRect: DOMRect;
  moved: boolean;
}

interface LeaderLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
}

// An image favorite: a background image (static upload or the newest capture of
// a Scoutview gateway) with labels positioned as a percent of the image size, so
// they stay in place at every display size. Dynamic labels show a live sensor
// value, refreshed (with dynamic images) every refreshSeconds. When
// labelsEditable, a "Labels" button opens the floating label editor and labels,
// arrow tips and region corners can be dragged on the image.
@Component({
  standalone: false,
  selector: 'kig-image-favorite',
  templateUrl: './image-favorite.component.html',
  styleUrls: ['./image-favorite.component.scss']
})
export class ImageFavoriteComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() imageFavorite: ImageFavoriteInfo;
  @Input() size: ImageFavoriteSize = 'Medium';

  // Fill the host's width instead of using the fixed Small / Medium / Large width, so the image
  // scales with its container and keeps its shape (the enlarged, resizable dashboard modal).
  @Input() fillWidth = false;
  @Input() labelsEditable = false;
  @Input() minimal = false;
  @Input() refreshSeconds = 300;
  @Input() username = 'unknown';

  // Draft mode (dashboard wizard): render an unsaved favorite. When draftLabels is
  // set the labels are local — edits change that array and are emitted through
  // draftLabelsChange instead of being saved (new labels get negative temporary
  // ids) — and draftImageSrc replaces the /Image lookup.
  @Input() draftImageSrc: string | null = null;
  @Input() draftLabels: ImageLabelInfo[] | null = null;
  @Output() draftLabelsChange = new EventEmitter<ImageLabelInfo[]>();

  // Emits when label editing starts/stops so a host can keep a single favorite in edit mode.
  @Output() labelsEditModeChange = new EventEmitter<boolean>();

  // Optional edit (pencil) icon at the far right of the header; the host decides
  // who may edit and what editing opens.
  @Input() showEditButton = false;
  @Output() editRequested = new EventEmitter<void>();

  // When true the header is a cdkDragHandle for the host's cdkDrag ancestor (the
  // dashboard's reorderable Favorites tiles). Off by default so hosts without a
  // cdkDrag -- or with a draggable modal around it -- are unaffected.
  @Input() dragHandle = false;

  @ViewChild('stage') stageRef: ElementRef<HTMLDivElement>;

  readonly LabelType = ImageLabelType;

  imageSrc: string | null = null;
  image: ImageFavoriteImage | null = null;
  imageLoading = false;
  labels: ImageLabelInfo[] = [];
  labelsEditMode = false;

  private values = new Map<number, ImageLabelValue>();

  // Rendered label-box size (percent of stage) used to start arrows at the box edge.
  private boxSizes = new Map<number, { w: number; h: number }>();
  private propsCache = new Map<number, { raw: string; props: ImageLabelProperties }>();
  private stageWidthPx = LABEL_BASE_WIDTH_PX;

  private drag: LabelDrag | null = null;
  private labelEditorRef: BsModalRef | null = null;
  private refreshSub: Subscription | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private destroy$ = new Subject<void>();

  // Same tile widths as the dashboard's Chart Favorites tiles.
  private static readonly SIZE_WIDTHS: { [size: string]: string } = {
    Small: '300px',
    Medium: '600px',
    Large: '1200px'
  };

  constructor(private imageFavoriteService: ImageFavoriteService,
              private modalService: BsModalService,
              private toastr: ToastrService,
              private zone: NgZone) {}


  get width(): string {
    if (this.fillWidth) {
      return '100%';
    }
    return ImageFavoriteComponent.SIZE_WIDTHS[this.size] ?? ImageFavoriteComponent.SIZE_WIDTHS.Medium;
  }

  get isDynamicImage(): boolean {
    return this.imageFavorite?.imageSourceType === ImageSourceType.Dynamic;
  }

  get isDraft(): boolean {
    return this.draftLabels != null;
  }

  // Font sizes are authored for a 600px-wide (Medium) image and scale with it.
  get fontScale(): number {
    return labelFontScale(this.stageWidthPx);
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imageFavorite'] != undefined) {
      this.exitLabelEditing();
      this.loadAll();
      this.startRefresh();
    } else {
      if (changes['refreshSeconds'] != undefined) {
        this.startRefresh();
      }
      if (changes['draftImageSrc'] != undefined && this.isDraft) {
        this.imageSrc = this.draftImageSrc;
        this.measureLabelsSoon();
      }
      if (changes['draftLabels'] != undefined && this.isDraft && this.draftLabels !== this.labels) {
        this.labels = this.draftLabels;
        this.pushLabelsToEditor();
        this.loadValues();
      }
    }

    if (changes['labelsEditable'] != undefined && !this.labelsEditable) {
      this.exitLabelEditing();
    }

    if (changes['size'] != undefined) {
      this.measureLabelsSoon();
    }
  }


  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.measureLabels()));
    this.resizeObserver.observe(this.stageRef.nativeElement);
  }


  ngOnDestroy(): void {
    this.exitLabelEditing();
    this.resizeObserver?.disconnect();
    this.refreshSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }


  // ----- Loading / refresh -----

  private loadAll(): void {
    this.imageSrc = null;
    this.image = null;
    this.labels = [];
    this.values.clear();
    this.boxSizes.clear();
    this.propsCache.clear();

    if (this.imageFavorite == undefined) {
      return;
    }

    if (this.isDraft) {
      this.imageSrc = this.draftImageSrc;
      this.labels = this.draftLabels;
      this.loadValues();
      this.measureLabelsSoon();
      return;
    }

    if (this.imageFavorite.id <= 0) {
      return;
    }

    this.loadImage();
    this.loadLabels();
    this.loadValues();
  }


  private loadImage(): void {
    const id = this.imageFavorite.id;
    this.imageLoading = this.imageSrc == null;
    this.imageFavoriteService.getImage(id).pipe(takeUntil(this.destroy$)).subscribe(
      (result: ImageFavoriteImage | null) => {
        if (this.imageFavorite?.id !== id) {
          return;
        }
        this.imageLoading = false;
        // The API returns UTC capture dates; display them in local time.
        if (result?.captureDate != undefined) {
          result.captureDate = moment.utc(result.captureDate).local().toDate();
        }
        this.image = result;
        this.imageSrc = (result != undefined && result.base64 != undefined && result.base64 !== '')
          ? `data:${result.contentType ?? 'image/jpeg'};base64,${result.base64}`
          : null;
      },
      _err => {
        this.imageLoading = false;
        this.toastr.error('Unable to load image for ' + this.imageFavorite?.name);
      });
  }


  private loadLabels(): void {
    const id = this.imageFavorite.id;
    this.imageFavoriteService.getImageLabels(id).pipe(takeUntil(this.destroy$)).subscribe(
      (results: ImageLabelInfo[] | null) => {
        if (this.imageFavorite?.id !== id) {
          return;
        }
        this.labels = results ?? [];
        this.pushLabelsToEditor();
        this.measureLabelsSoon();
      },
      _err => this.toastr.error('Unable to load labels for ' + this.imageFavorite?.name));
  }


  private loadValues(): void {
    const id = this.imageFavorite.id;
    const values$ = this.isDraft
      ? this.imageFavoriteService.getPreviewLabelValues(this.labels.filter(l => l.isDynamic))
      : this.imageFavoriteService.getLabelValues(id);
    values$.pipe(takeUntil(this.destroy$)).subscribe(
      (results: ImageLabelValue[] | null) => {
        if (this.imageFavorite?.id !== id) {
          return;
        }
        this.values = new Map((results ?? []).map(v => [v.labelId, v]));
        this.measureLabelsSoon();
      },
      _err => { });
  }


  // Periodic refresh of dynamic values (and of the newest gateway image for
  // dynamic favorites). Skipped mid-drag so a reload can't fight the user.
  private startRefresh(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = null;
    if (this.imageFavorite == undefined || !(this.refreshSeconds > 0)) {
      return;
    }
    const period = this.refreshSeconds * 1000;
    this.refreshSub = timer(period, period).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.drag != null || this.imageFavorite == undefined) {
        return;
      }
      if (this.labels.some(l => l.isDynamic)) {
        this.loadValues();
      }
      // A draft's image is owned by its host (the wizard fetches the preview).
      if (this.isDynamicImage && !this.isDraft) {
        this.loadImage();
      }
    });
  }


  // ----- Rendering helpers -----

  propsOf(label: ImageLabelInfo): ImageLabelProperties {
    const raw = label.properties ?? '';
    const cached = this.propsCache.get(label.id);
    if (cached != undefined && cached.raw === raw) {
      return cached.props;
    }
    const props = parseLabelProps(label);
    this.propsCache.set(label.id, { raw, props });
    return props;
  }


  labelStyle(label: ImageLabelInfo): { [key: string]: any } {
    const style = labelBoxStyle(label, this.propsOf(label), this.fontScale);
    style['left.%'] = label.boxX;
    style['top.%'] = label.boxY;

    if (label.labelType === ImageLabelType.Region) {
      style['width.%'] = label.boxWidth ?? 30;
      style['height.%'] = label.boxHeight ?? 30;
    } else if (label.boxWidth != undefined) {
      style['width.%'] = label.boxWidth;
    }
    return style;
  }


  valueStyle(label: ImageLabelInfo): { [key: string]: any } {
    return labelValueStyle(this.propsOf(label), this.fontScale);
  }


  formatValue(label: ImageLabelInfo): string {
    return formatLabelValue(this.propsOf(label), this.values.get(label.id));
  }


  valueTooltip(label: ImageLabelInfo): string {
    return labelValueTooltip(this.values.get(label.id));
  }


  showsTarget(label: ImageLabelInfo): boolean {
    return this.typeHasArrow(label.labelType) && this.propsOf(label).showArrow === true &&
           label.targetX != undefined && label.targetY != undefined;
  }


  // Arrow from the nearest point of each label's box to its target.
  get leaderLines(): LeaderLine[] {
    return this.labels.filter(l => this.showsTarget(l)).map(l => {
      const size = this.boxSizes.get(l.id) ?? { w: 0, h: 0 };
      const x1 = Math.min(Math.max(l.targetX, l.boxX), l.boxX + size.w);
      const y1 = Math.min(Math.max(l.targetY, l.boxY), l.boxY + size.h);
      const p = this.propsOf(l);
      return { x1, y1, x2: l.targetX, y2: l.targetY, color: arrowColor(p) };
    });
  }


  trackById(_index: number, label: ImageLabelInfo): number {
    return label.id;
  }


  trackByIndex(index: number): number {
    return index;
  }


  // ----- Label editing (button -> editor modal + on-image dragging) -----

  toggleLabelEditing(): void {
    if (this.labelsEditMode) {
      this.exitLabelEditing();
    } else {
      this.enterLabelEditing();
    }
  }


  private enterLabelEditing(): void {
    if (!this.labelsEditable || this.imageFavorite == undefined) {
      return;
    }
    this.labelsEditMode = true;
    this.labelsEditModeChange.emit(true);
    this.labelEditorRef = this.modalService.show(ImageLabelEditorModalComponent, {
      animated: false,
      backdrop: false,
      ignoreBackdropClick: true,
      class: 'image-label-editor-dialog',
      initialState: { labels: this.labels }
    });
    const content = this.labelEditorRef.content as ImageLabelEditorModalComponent;
    content.onAdd.subscribe((type: number) => this.addLabel(type));
    content.onUpdate.subscribe((label: ImageLabelInfo) => this.persistLabel(label, true));
    content.onDelete.subscribe((id: number) => this.deleteLabel(id));
    content.onClosed.subscribe(() => this.exitLabelEditing());
  }


  private exitLabelEditing(): void {
    if (!this.labelsEditMode) {
      return;
    }
    this.labelsEditMode = false;
    this.drag = null;
    if (this.labelEditorRef != undefined) {
      this.labelEditorRef.hide();
      this.labelEditorRef = null;
    }
    this.labelsEditModeChange.emit(false);
  }


  private pushLabelsToEditor(selectId?: number): void {
    if (this.labelEditorRef == undefined || this.labelEditorRef.content == undefined) {
      return;
    }
    const content = this.labelEditorRef.content as ImageLabelEditorModalComponent;
    content.labels = this.labels;
    if (selectId != undefined) {
      content.selectById(selectId);
    }
  }


  // Create a new label of the given type near the image center (the box offset
  // up-left from the target so the arrow is visible).
  private addLabel(type: number): void {
    if (this.imageFavorite == undefined || (!this.isDraft && this.imageFavorite.id <= 0)) {
      return;
    }

    const props = newLabelProps(type);

    const request: ImageLabelInfo = {
      id: 0,
      imageFavoriteID: this.imageFavorite.id,
      labelType: type,
      text: type === ImageLabelType.Region ? '' : 'New label',
      boxX: 30,
      boxY: 30,
      boxWidth: null,
      boxHeight: null,
      targetX: null,
      targetY: null,
      properties: JSON.stringify(props),
      isDynamic: false,
      sensorID: null,
      sensorReadingTypeID: null,
      aggregationType: null,
      aggregationMinutes: null
    };
    this.ensureGeometryForType(request);

    if (this.isDraft) {
      const tempId = Math.min(0, ...this.labels.map(l => l.id)) - 1;
      this.labels = [...this.labels, { ...request, id: tempId }];
      this.draftLabelsChange.emit(this.labels);
      this.pushLabelsToEditor(tempId);
      this.measureLabelsSoon();
      return;
    }

    this.imageFavoriteService.createImageLabel(request).subscribe(
      (newId: any) => {
        const id = Number(newId);
        if (isNaN(id) || id <= 0) {
          this.toastr.error('Unable to add label');
          return;
        }
        this.labels = [...this.labels, { ...request, id }];
        this.pushLabelsToEditor(id);
        this.measureLabelsSoon();
      },
      _err => this.toastr.error('Unable to add label'));
  }


  // Persist a label's current state. Editor changes (reload true) may have made
  // the label dynamic or changed its sensor, so its value is refetched.
  private persistLabel(label: ImageLabelInfo, reload: boolean): void {
    if (label == undefined || (!this.isDraft && label.id <= 0)) {
      return;
    }
    if (reload) {
      this.ensureGeometryForType(label);
    }

    // Draft labels are edited in place; the host saves them later.
    if (this.isDraft) {
      this.draftLabelsChange.emit(this.labels);
      if (reload && label.isDynamic) {
        this.loadValues();
      }
      this.measureLabelsSoon();
      return;
    }

    this.imageFavoriteService.updateImageLabel(label, this.username).subscribe(
      () => {
        if (reload && label.isDynamic) {
          this.loadValues();
        }
      },
      _err => this.toastr.error('Unable to save label'));

    this.measureLabelsSoon();
  }


  // Seed any geometry a label's current type needs but is missing — e.g. after
  // the user switches a label's type in the editor.
  private ensureGeometryForType(label: ImageLabelInfo): void {
    if (this.typeHasArrow(label.labelType) && (label.targetX == undefined || label.targetY == undefined)) {
      label.targetX = this.clamp(label.boxX + 20, 0, 100);
      label.targetY = this.clamp(label.boxY + 20, 0, 100);
    }
    if (label.labelType === ImageLabelType.Region && (label.boxWidth == undefined || label.boxHeight == undefined)) {
      label.boxWidth = this.clamp(30, 2, 100 - label.boxX);
      label.boxHeight = this.clamp(30, 2, 100 - label.boxY);
    }
  }


  private deleteLabel(id: number): void {
    if (this.isDraft) {
      this.labels = this.labels.filter(l => l.id !== id);
      this.draftLabelsChange.emit(this.labels);
    } else {
      if (id <= 0) {
        return;
      }
      this.imageFavoriteService.deleteImageLabel(id, this.username).subscribe(
        () => { },
        _err => this.toastr.error('Unable to delete label'));
      this.labels = this.labels.filter(l => l.id !== id);
    }

    this.values.delete(id);
    this.boxSizes.delete(id);
    this.pushLabelsToEditor();
  }


  // ----- Dragging (pointer offsets converted to percent of the image) -----

  startDrag(event: PointerEvent, label: ImageLabelInfo, mode: 'box' | 'target' | 'resize'): void {
    if (!this.labelsEditMode || event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    this.drag = {
      label,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      origX: mode === 'box' ? label.boxX : mode === 'target' ? label.targetX : (label.boxWidth ?? 30),
      origY: mode === 'box' ? label.boxY : mode === 'target' ? label.targetY : (label.boxHeight ?? 30),
      stageRect: this.stageRef.nativeElement.getBoundingClientRect(),
      moved: false
    };

    // Clicking a label on the image also selects it in the editor.
    const content = this.labelEditorRef?.content as ImageLabelEditorModalComponent;
    content?.selectById(label.id);
  }


  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (this.drag == null) {
      return;
    }
    const d = this.drag;
    const dx = (event.clientX - d.startX) / d.stageRect.width * 100;
    const dy = (event.clientY - d.startY) / d.stageRect.height * 100;
    d.moved = true;

    if (d.mode === 'box') {
      const size = this.boxSizes.get(d.label.id) ?? { w: 0, h: 0 };
      d.label.boxX = this.round(this.clamp(d.origX + dx, 0, Math.max(0, 100 - size.w)));
      d.label.boxY = this.round(this.clamp(d.origY + dy, 0, Math.max(0, 100 - size.h)));
    } else if (d.mode === 'target') {
      d.label.targetX = this.round(this.clamp(d.origX + dx, 0, 100));
      d.label.targetY = this.round(this.clamp(d.origY + dy, 0, 100));
    } else {
      d.label.boxWidth = this.round(this.clamp(d.origX + dx, 2, 100 - d.label.boxX));
      d.label.boxHeight = this.round(this.clamp(d.origY + dy, 2, 100 - d.label.boxY));
    }
  }


  @HostListener('window:pointerup')
  onPointerUp(): void {
    if (this.drag == null) {
      return;
    }
    const d = this.drag;
    this.drag = null;
    if (d.moved) {
      this.persistLabel(d.label, false);
    }
  }


  // ----- Measuring -----

  measureLabelsSoon(): void {
    setTimeout(() => this.measureLabels(), 0);
  }


  private measureLabels(): void {
    const stage = this.stageRef?.nativeElement;
    if (stage == undefined || stage.clientWidth === 0 || stage.clientHeight === 0) {
      return;
    }
    this.stageWidthPx = stage.clientWidth;
    stage.querySelectorAll<HTMLElement>('[data-label-id]').forEach(el => {
      const id = Number(el.dataset['labelId']);
      this.boxSizes.set(id, {
        w: el.offsetWidth / stage.clientWidth * 100,
        h: el.offsetHeight / stage.clientHeight * 100
      });
    });
  }


  private typeHasArrow(labelType: number): boolean {
    return labelTypeHasArrow(labelType);
  }


  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }


  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
